import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  Network,
  Orbit,
  Radar,
  Search,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import NetworkMap from './NetworkMap';
import { OcupacaoRedeDataProvider, useOcupacaoRedeData } from '../context/OcupacaoRedeDataContext';
import {
  applyOcupacaoFilters,
  buildOcupacaoDashboardSnapshot,
  buildOccupancyFilterLabel,
  getOccupancyRangeLabel,
  OCUPACAO_FILTER_FIELDS,
} from '../utils/ocupacaoRedeAnalytics';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';

const currencylessNumber = new Intl.NumberFormat('pt-BR');

const ACCENT_PRIMARY = 'var(--accent)';
const ACCENT_SOFT = 'color-mix(in srgb, var(--accent) 78%, white 22%)';
const ACCENT_STRONG = 'color-mix(in srgb, var(--accent) 72%, black 28%)';
const pieColors = [ACCENT_PRIMARY, ACCENT_STRONG];

const STATUS_META = {
  Baixa: {
    label: 'Baixa',
    tone: 'text-emerald-500',
    chip: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
  },
  Media: {
    label: 'Média',
    tone: 'text-amber-500',
    chip: 'border-amber-400/30 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  },
  Critica: {
    label: 'Crítica',
    tone: 'text-rose-500',
    chip: 'border-rose-400/30 bg-rose-500/10 text-rose-600 dark:text-rose-300',
  },
};

const OCCUPANCY_RANGE_OPTIONS = [
  { value: 'Baixa', label: getOccupancyRangeLabel('Baixa') },
  { value: 'Media', label: getOccupancyRangeLabel('Media') },
  { value: 'Critica', label: getOccupancyRangeLabel('Critica') },
];
const POP_ORDER_OPTIONS = [
  { value: 'ocupadas', label: 'Portas ocupadas' },
  { value: 'ocupacao', label: '% de ocupação' },
  { value: 'vagas', label: 'Portas livres' },
  { value: 'capacidade', label: 'Capacidade total' },
  { value: 'count', label: 'Quantidade de CTOs' },
];
const POP_VIEW_MODES = [
  { value: 'geral', label: 'Visão geral' },
  { value: 'alerta', label: 'Alerta > 80%' },
];
const MAP_SYNC_DEBOUNCE_MS = 180;

const getActiveOccupancyFilterValues = (filters, field) => filters
  .filter((filter) => filter.field === field)
  .map((filter) => String(filter.value));


const buildFieldRowsWithoutFilter = (rows, filters, field) => applyOcupacaoFilters(
  rows,
  filters.filter((filter) => filter.field !== field),
);

const buildCtoAxisLabel = (label) => {
  const text = String(label || '').trim();

  if (!text) {
    return 'CTO';
  }

  const parts = text.split('_').filter(Boolean);

  if (parts.length >= 4) {
    return `${parts[1]} / ${parts[2]}`;
  }

  if (parts.length >= 2) {
    return `${parts[0]} / ${parts[1]}`;
  }

  return text.length > 16 ? `${text.slice(0, 16)}...` : text;
};

const CapacityPressureTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length || !payload[0] || !payload[0].payload) {
    return null;
  }

  const row = payload[0].payload;
  const overflow = row.overflow || 0;

  return (
    <div style={chartTooltipStyle} className="min-w-[220px] p-3">
      <p className="text-sm font-semibold text-[color:var(--text-primary)]">{row.label}</p>
      <div className="mt-2 space-y-1 text-xs text-[color:var(--text-muted)]">
        <p><span className="font-medium text-[color:var(--text-primary)]">Cidade:</span> {row.cidade}</p>
        <p><span className="font-medium text-[color:var(--text-primary)]">Projeto:</span> {row.projeto}</p>
        <p><span className="font-medium text-[color:var(--text-primary)]">POP:</span> {row.pop}</p>
      </div>
      <div className="mt-3 border-t border-[color:var(--border)] pt-3 text-sm text-[color:var(--text-primary)]">
        <p>Pontos ocupados: {currencylessNumber.format(row.ocupadas)}</p>
        <p>Capacidade total: {currencylessNumber.format(row.capacidade)}</p>
        <p>Portas livres: {currencylessNumber.format(row.vagasDisplay)}</p>
        {overflow > 0 ? (
          <p className="mt-1 font-medium text-rose-400">Excedente de capacidade: {currencylessNumber.format(overflow)}</p>
        ) : null}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, hint, icon: Icon, accent }) => (
  <div className={`${panelClassName} relative min-h-[138px] overflow-hidden`}>
    <div
      className="pointer-events-none absolute inset-x-5 top-0 h-px"
      style={{ background: accent }}
    />
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--text-muted)]">{label}</p>
        <p className="mt-3 text-3xl font-semibold leading-none text-[color:var(--text-primary)] sm:text-4xl">
          {currencylessNumber.format(value)}
        </p>
        <p className="mt-2 text-xs text-[color:var(--text-muted)]">{hint}</p>
      </div>
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 text-[color:var(--accent)]">
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </div>
);

const ProjectOccupancyPanel = ({ rows, onToggleProjectFilter, activeProjects }) => (
  <div className={`${panelClassName} h-full`}>
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Portas por projeto</h3>
        <p className="mt-1 text-xs text-[color:var(--text-muted)]">
          Todos os projetos do recorte com ocupação, capacidade total, portas livres e percentual consolidado.
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
          Clique para cruzar filtros por projeto
        </p>
      </div>
      <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
        {currencylessNumber.format(rows.length)} projetos
      </span>
    </div>

    <div className="app-scroll-subtle space-y-3 overflow-y-auto pr-1 xl:max-h-[760px]">
      {rows.map((row) => {
        const isActive = activeProjects.includes(row.label);

        return (
          <button
            key={row.label}
            type="button"
            onClick={() => onToggleProjectFilter(row.label)}
            aria-pressed={isActive}
            className={`w-full rounded-[18px] border px-4 py-3.5 text-left transition-all ${
              isActive
                ? 'border-[color:var(--accent)] bg-[color:var(--surface)]/96 shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_22%,transparent)]'
                : 'border-[color:var(--border)] bg-[color:var(--surface-strong)] hover:border-[color:var(--accent)]/38'
            }`}
          >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                {row.label}
              </p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                {row.count} CTOs vinculadas
              </p>
            </div>

            <div className="shrink-0 text-right">
              {isActive ? (
                <span className="mb-2 inline-flex rounded-full border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--accent)]">
                  Filtro ativo
                </span>
              ) : null}
              <p className="text-lg font-semibold text-[color:var(--text-primary)]">
                {row.percent.toFixed(1)}%
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                ocupação
              </p>
            </div>
          </div>

          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[color:var(--surface-muted)]">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, row.percent)}%`,
                background: `linear-gradient(90deg, ${ACCENT_SOFT} 0%, ${ACCENT_STRONG} 100%)`,
              }}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Ocupadas</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                {currencylessNumber.format(row.ocupadas)}
              </p>
            </div>
            <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Livres</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                {currencylessNumber.format(row.vagas)}
              </p>
            </div>
            <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Total</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                {currencylessNumber.format(row.capacidade)}
              </p>
            </div>
            <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Percentual</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                {row.percent.toFixed(1)}%
              </p>
            </div>
          </div>
          </button>
        );
      })}

      {rows.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-[color:var(--border)] px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
          Nenhum projeto encontrado para o recorte atual.
        </div>
      ) : null}
    </div>
  </div>
);

const PopOccupancyPanel = ({
  rows,
  orderBy,
  onOrderChange,
  viewMode,
  onViewModeChange,
  searchTerm,
  onSearchTermChange,
  onTogglePopFilter,
  activePops,
}) => (
  <div className={`${panelClassName} h-full`}>
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Portas por POP</h3>
          <p className="mt-1 text-xs text-[color:var(--text-muted)]">
            Leitura de saturação por backbone com busca, ordenação, alerta e filtro direto na aba.
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
            Clique para cruzar filtros por POP
          </p>
        </div>
        <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
          {currencylessNumber.format(rows.length)} POPs
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-[18px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-4 py-3 sm:col-span-2">
          <label className="block" htmlFor="ocupacao-pop-search">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">
              Buscar POP
            </span>
            <div className="relative mt-2">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]"
              />
              <input
                id="ocupacao-pop-search"
                type="search"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                placeholder="Ex.: PA_STM_AR07..."
                className="h-10 w-full rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] py-2 pl-10 pr-10 text-sm text-[color:var(--text-primary)] outline-none transition placeholder:text-[color:var(--text-muted)]/70 focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => onSearchTermChange('')}
                  className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-strong)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
                  aria-label="Limpar busca por POP"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </label>
        </div>

        <FilterSelect
          label="Ordenar por"
          value={orderBy}
          options={POP_ORDER_OPTIONS}
          onChange={onOrderChange}
        />

        <FilterSelect
          label="Modo"
          value={viewMode}
          options={POP_VIEW_MODES}
          onChange={onViewModeChange}
        />
      </div>
    </div>

    <div className="app-scroll-subtle space-y-3 overflow-y-auto pr-1 xl:max-h-[760px]">
      {rows.map((row) => {
        const occupiedPercent = row.capacidade > 0 ? (row.ocupadas / row.capacidade) * 100 : 0;
        const vacantPercent = row.capacidade > 0 ? (row.vagas / row.capacidade) * 100 : 0;
        const isAlert = row.percent > 80;
        const isActive = activePops.includes(row.label);

        return (
          <button
            key={row.label}
            type="button"
            onClick={() => onTogglePopFilter(row.label)}
            className={`w-full rounded-[18px] border px-4 py-3.5 text-left transition-all ${
              isActive
                ? 'border-[color:var(--accent)] bg-[color:var(--surface)]/96 shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_22%,transparent)]'
                : 'border-[color:var(--border)] bg-[color:var(--surface-strong)] hover:border-[color:var(--accent)]/38'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-base font-semibold text-[color:var(--text-primary)]">
                    {row.label}
                  </p>
                  {isAlert ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-rose-300">
                      <AlertTriangle className="h-3 w-3" />
                      Alerta
                    </span>
                  ) : null}
                  {isActive ? (
                    <span className="inline-flex rounded-full border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--accent)]">
                      Filtro ativo
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                  {row.count} CTOs no backbone
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className={`text-lg font-semibold ${isAlert ? 'text-rose-300' : 'text-[color:var(--text-primary)]'}`}>
                  {row.percent.toFixed(1)}%
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                  ocupação
                </p>
              </div>
            </div>

            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[color:var(--surface-muted)]">
              <div className="flex h-full">
                <div
                  style={{
                    width: `${Math.min(100, occupiedPercent)}%`,
                    background: isAlert ? 'linear-gradient(90deg, #fb7185 0%, #f43f5e 100%)' : ACCENT_SOFT,
                  }}
                />
                <div
                  style={{
                    width: `${Math.min(100, vacantPercent)}%`,
                    background: isAlert ? 'rgba(244,63,94,0.16)' : ACCENT_STRONG,
                    opacity: isAlert ? 0.45 : 1,
                  }}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Ocupadas</p>
                <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                  {currencylessNumber.format(row.ocupadas)}
                </p>
              </div>
              <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Livres</p>
                <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                  {currencylessNumber.format(row.vagas)}
                </p>
              </div>
              <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Total</p>
                <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                  {currencylessNumber.format(row.capacidade)}
                </p>
              </div>
              <div className="rounded-[14px] border border-[color:var(--border)] bg-[color:var(--surface)]/72 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">Saldo</p>
                <p className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                  {currencylessNumber.format(Math.max(row.capacidade - row.ocupadas, 0))}
                </p>
              </div>
            </div>
          </button>
        );
      })}

      {rows.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-[color:var(--border)] px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
          Nenhum POP encontrado para os critérios atuais.
        </div>
      ) : null}
    </div>
  </div>
);

const CityPressurePanel = ({ rows, isMobile = false, onToggleCityFilter, activeCities }) => (
  <div className={`${panelClassName} min-h-[280px] xl:min-h-[500px]`}>
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Leitura por cidade</h3>
        <p className="mt-1 text-xs text-[color:var(--text-muted)]">
          Percentual de ocupação com volume ocupado e carteira de vagas por praça.
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
          Clique para cruzar filtros por cidade
        </p>
      </div>
      <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
        Top 8
      </span>
    </div>

    <div className="space-y-2.5 app-scroll-subtle xl:max-h-[560px] xl:overflow-y-auto xl:pr-1">
      {rows.map((row) => {
        const occupiedPercent = row.total > 0 ? (row.ocupadas / row.total) * 100 : 0;
        const vacantPercent = row.total > 0 ? (row.vagas / row.total) * 100 : 0;
        const isActive = activeCities.includes(row.label);

        return (
          <button
            key={row.label}
            type="button"
            onClick={() => onToggleCityFilter(row.label)}
            aria-pressed={isActive}
            className={`w-full rounded-[16px] border px-3 py-3 text-left transition-all ${
              isActive
                ? 'border-[color:var(--accent)] bg-[color:var(--surface)]/96 shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_22%,transparent)]'
                : 'border-[color:var(--border)] bg-[color:var(--surface-strong)] hover:border-[color:var(--accent)]/38'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
                  {row.label}
                </p>
                <p className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                  {currencylessNumber.format(row.ocupadas)} ocupadas · {currencylessNumber.format(row.vagas)} vagas
                </p>
              </div>
              <div className="shrink-0 text-right">
              {isActive ? (
                <span className="mb-2 inline-flex rounded-full border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--accent)]">
                  Filtro ativo
                </span>
              ) : null}
                <p className="text-sm font-semibold text-[color:var(--text-primary)]">
                  {row.percent.toFixed(1)}%
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                  ocupação
                </p>
              </div>
            </div>

            <div className="mt-3">
              <div className="flex h-2.5 overflow-hidden rounded-full bg-[color:var(--surface-muted)]">
                <div
                  className="rounded-l-full"
                  style={{
                    width: `${Math.min(100, occupiedPercent)}%`,
                    background: ACCENT_SOFT,
                  }}
                />
                <div
                  className="rounded-r-full"
                  style={{
                    width: `${Math.min(100, vacantPercent)}%`,
                    background: ACCENT_STRONG,
                  }}
                />
              </div>
              <div className={`mt-2 grid gap-2 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)] ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-flex h-2 w-2 rounded-full"
                    style={{ background: ACCENT_SOFT }}
                    aria-hidden="true"
                  />
                  Ocupadas
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-flex h-2 w-2 rounded-full"
                    style={{ background: ACCENT_STRONG }}
                    aria-hidden="true"
                  />
                  Vagas
                </span>
              </div>
            </div>
          </button>
        );
      })}

      {rows.length === 0 ? (
        <div className="rounded-[16px] border border-dashed border-[color:var(--border)] px-3 py-6 text-center text-sm text-[color:var(--text-muted)]">
          Nenhuma cidade encontrada para o recorte atual.
        </div>
      ) : null}
    </div>
  </div>
);

const OccupancyBandsOverview = ({ rows, total, onToggleRangeFilter, activeRanges }) => (
  <div className={panelClassName}>
    <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Radar de criticidade</h3>
        <p className="mt-1 text-xs text-[color:var(--text-muted)]">
          Distribuição das CTOs filtradas por faixa de ocupação, com leitura imediata de risco operacional.
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
          Clique em uma faixa para combinar filtros
        </p>
      </div>
      <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
        {currencylessNumber.format(total)} CTOs analisadas
      </span>
    </div>

    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {rows.map((item) => {
        const isActive = activeRanges.includes(item.key);

        return (
        <button
          key={item.key}
          type="button"
          onClick={() => onToggleRangeFilter(item.key)}
          aria-pressed={isActive}
          className={`group relative overflow-hidden rounded-[20px] border px-4 py-4 text-left transition-all ${
            isActive
              ? 'border-[color:var(--accent)] bg-[color:var(--surface)]/96 shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent)_22%,transparent)]'
              : 'border-[color:var(--border)] bg-[color:var(--surface-strong)] hover:border-[color:var(--accent)]/38'
          }`}
        >
          <div
            className="pointer-events-none absolute inset-x-4 top-0 h-px opacity-80"
            style={{
              background:
                item.key === 'Critica'
                  ? 'linear-gradient(90deg, transparent 0%, rgba(244,63,94,0.9) 50%, transparent 100%)'
                  : item.key === 'Media'
                    ? 'linear-gradient(90deg, transparent 0%, rgba(245,158,11,0.9) 50%, transparent 100%)'
                    : 'linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.9) 50%, transparent 100%)',
            }}
          />

          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col items-start gap-2">
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${STATUS_META[item.key].chip}`}>
                {item.label}
              </span>
              {isActive ? (
                <span className="inline-flex rounded-full border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--accent)]">
                  Filtro ativo
                </span>
              ) : null}
            </div>
            <div className="text-right">
              <p className={`text-2xl font-semibold leading-none ${STATUS_META[item.key].tone}`}>
                {currencylessNumber.format(item.count)}
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                {item.percent.toFixed(1)}% do total
              </p>
            </div>
          </div>

          <div className="mt-4">
            <div className="h-2.5 overflow-hidden rounded-full bg-[color:var(--surface-muted)]">
              <div
                className={`h-full rounded-full ${
                  item.key === 'Critica' ? 'bg-rose-500' : item.key === 'Media' ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(item.percent, 100)}%` }}
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-[color:var(--text-muted)]">
              <span>{item.meta}</span>
              <span className="font-medium text-[color:var(--text-primary)]">
                {item.key === 'Critica' ? 'Ação prioritária' : item.key === 'Media' ? 'Monitorar' : 'Estável'}
              </span>
            </div>
          </div>
          </button>
        );
      })}

      {rows.length === 0 ? (
        <div className="rounded-[20px] border border-dashed border-[color:var(--border)] px-4 py-8 text-center text-sm text-[color:var(--text-muted)] md:col-span-3">
          Nenhuma faixa de ocupação disponível para o recorte atual.
        </div>
      ) : null}
    </div>
  </div>
);

const FilterSelect = ({ label, value, options, onChange, displayLabel, helperText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const normalizedOptions = useMemo(
    () => options.map((option) => (
      typeof option === 'object' && option !== null
        ? option
        : { value: option, label: option }
    )),
    [options],
  );

  const selectedOption = normalizedOptions.find((option) => String(option.value) === String(value))
    || { value: '', label: 'Todos' };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const allOptions = useMemo(
    () => [{ value: '', label: 'Todos' }, ...normalizedOptions],
    [normalizedOptions],
  );

  return (
    <div ref={containerRef} className="relative flex min-w-0 flex-col gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--text-muted)]">
        {label}
      </span>

      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={`group relative flex min-h-[44px] w-full items-center justify-between gap-3 overflow-hidden rounded-[18px] border px-4 py-3 text-left transition-all duration-200 ${
          isOpen
            ? 'border-[color:var(--accent)] bg-[color:var(--surface-strong)]'
            : 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)]/92 hover:border-[color:var(--accent)]/40 hover:bg-[color:var(--surface-muted)]/72'
        }`}
        style={isOpen ? { boxShadow: '0 0 0 1px color-mix(in srgb, var(--accent) 18%, transparent), 0 18px 36px rgba(15,23,42,0.18)' } : undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <div
          className={`pointer-events-none absolute inset-x-4 top-0 h-px transition-opacity duration-200 ${
            isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-70'
          }`}
          style={{
            background: 'linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--accent) 82%, white 18%) 50%, transparent 100%)',
          }}
        />

        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[color:var(--text-primary)]">
            {displayLabel || selectedOption.label}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
            {helperText || (value ? 'Filtro ativo' : 'Todos os registros')}
          </div>
        </div>

        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/68 text-[color:var(--text-muted)] transition-colors duration-200 group-hover:text-[color:var(--accent)]">
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[color:var(--accent)]' : ''}`} />
        </span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-30 overflow-hidden rounded-[20px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)]/96 p-2 shadow-[0_28px_60px_rgba(2,6,23,0.26)] backdrop-blur-xl">
          <div className="mb-2 flex items-center justify-between px-2 pt-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
              {label}
            </span>
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2 py-0.5 text-[10px] text-[color:var(--text-muted)]">
              {allOptions.length} opções
            </span>
          </div>

          <div
            role="listbox"
            aria-label={label}
            className="app-scroll-subtle max-h-[280px] space-y-1 overflow-y-auto pr-1"
          >
            {allOptions.map((option) => {
              const isMultiValueDisplay = Boolean(displayLabel) && value === '';
              const isSelected = isMultiValueDisplay
                ? String(option.value) !== '' && String(option.value) === String(value)
                : String(option.value) === String(value);

              return (
                <button
                  key={`${label}-${option.value || 'all'}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-[14px] px-3 py-2.5 text-left transition-all duration-150 ${
                    isSelected
                      ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                      : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/72'
                  }`}
                  style={isSelected ? { boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--accent) 22%, transparent)' } : undefined}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{option.label}</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
                      {option.value ? 'Aplicar filtro' : 'Limpar este filtro'}
                    </div>
                  </div>

                  <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                    isSelected
                      ? 'border-[color:var(--accent)] bg-[color:var(--accent)]/12 text-[color:var(--accent)]'
                      : 'border-[color:var(--header-control-border-subtle)] text-transparent'
                  }`}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const OcupacaoRedeDashboardContent = ({ isMobile = false }) => {
  const {
    rowsRaw,
    rowsFiltered,
    filters,
    loading,
    errorMessage,
    retry,
    setFieldFilter,
    toggleFilter,
    removeFilter,
    clearFilters,
  } = useOcupacaoRedeData();
  const dashboardSnapshot = useMemo(
    () => buildOcupacaoDashboardSnapshot(rowsFiltered),
    [rowsFiltered],
  );
  const summary = dashboardSnapshot.summary;
  const [mapRows, setMapRows] = useState(rowsFiltered);
  const [isMapSyncing, setIsMapSyncing] = useState(false);
  const [popSearch, setPopSearch] = useState('');
  const [popOrderBy, setPopOrderBy] = useState('ocupadas');
  const [popViewMode, setPopViewMode] = useState('geral');

  useEffect(() => {
    if (mapRows === rowsFiltered) {
      setIsMapSyncing(false);
      return undefined;
    }

    setIsMapSyncing(true);

    let debounceId = 0;
    let idleId = 0;
    let cancelled = false;

    const commitRows = () => {
      if (cancelled) {
        return;
      }

      setMapRows(rowsFiltered);
      setIsMapSyncing(false);
    };

    const scheduleCommit = () => {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(commitRows, { timeout: 450 });
        return;
      }

      idleId = window.setTimeout(commitRows, 0);
    };

    debounceId = window.setTimeout(scheduleCommit, MAP_SYNC_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(debounceId);

      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, [mapRows, rowsFiltered]);

  const cidadeOptions = useMemo(
    () => Array.from(new Set(rowsRaw.map((row) => row.cidade))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rowsRaw],
  );

  const projetoOptions = useMemo(
    () => Array.from(new Set(rowsRaw.map((row) => row.projeto))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rowsRaw],
  );

  const popOptions = useMemo(
    () => Array.from(new Set(rowsRaw.map((row) => row.pop))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rowsRaw],
  );

  const activeFiltersByField = useMemo(
    () => filters.reduce((acc, filter) => {
      if (!acc[filter.field]) {
        acc[filter.field] = [];
      }

      acc[filter.field].push(String(filter.value));
      return acc;
    }, {}),
    [filters],
  );

  const rowsWithoutCidadeFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsRaw, filters, OCUPACAO_FILTER_FIELDS.cidade),
    [filters, rowsRaw],
  );
  const rowsWithoutProjetoFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsRaw, filters, OCUPACAO_FILTER_FIELDS.projeto),
    [filters, rowsRaw],
  );
  const rowsWithoutPopFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsRaw, filters, OCUPACAO_FILTER_FIELDS.pop),
    [filters, rowsRaw],
  );
  const rowsWithoutFaixaFilter = useMemo(
    () => buildFieldRowsWithoutFilter(rowsRaw, filters, OCUPACAO_FILTER_FIELDS.faixa_ocupacao),
    [filters, rowsRaw],
  );

  const citySnapshot = useMemo(() => buildOcupacaoDashboardSnapshot(rowsWithoutCidadeFilter), [rowsWithoutCidadeFilter]);
  const projectSnapshot = useMemo(() => buildOcupacaoDashboardSnapshot(rowsWithoutProjetoFilter), [rowsWithoutProjetoFilter]);
  const popSnapshot = useMemo(() => buildOcupacaoDashboardSnapshot(rowsWithoutPopFilter), [rowsWithoutPopFilter]);
  const faixaSnapshot = useMemo(() => buildOcupacaoDashboardSnapshot(rowsWithoutFaixaFilter), [rowsWithoutFaixaFilter]);

  const cityMix = useMemo(() => citySnapshot.groupedRows.cidade.slice(0, 8), [citySnapshot]);
  const projectRows = useMemo(() => (
    projectSnapshot.groupedRows.projeto.map((item) => ({
      label: item.label,
      percent: item.ocupacao,
      ocupadas: item.ocupadas,
      vagas: item.vagas,
      capacidade: item.capacidade,
      total: item.total,
      count: item.count,
    }))
  ), [projectSnapshot]);
  const popRows = useMemo(() => {
    const search = popSearch.trim().toLowerCase();

    const sortedRows = [...popSnapshot.groupedRows.pop].sort((left, right) => {
      const sorters = {
        ocupadas: () => right.ocupadas - left.ocupadas,
        ocupacao: () => right.ocupacao - left.ocupacao,
        vagas: () => right.vagas - left.vagas,
        capacidade: () => right.capacidade - left.capacidade,
        count: () => right.count - left.count,
      };

      const primarySorter = sorters[popOrderBy] || sorters.ocupadas;
      const primaryResult = primarySorter();

      if (primaryResult !== 0) {
        return primaryResult;
      }

      return left.label.localeCompare(right.label, 'pt-BR');
    });

    return sortedRows
      .filter((item) => (popViewMode === 'alerta' ? item.ocupacao > 80 : true))
      .filter((item) => (search ? item.label.toLowerCase().includes(search) : true))
      .map((item) => ({
        label: item.label,
        percent: item.ocupacao,
        ocupadas: item.ocupadas,
        vagas: item.vagas,
        capacidade: item.capacidade,
        total: item.total,
        count: item.count,
      }));
  }, [popOrderBy, popSearch, popSnapshot, popViewMode]);

  const occupancySummary = useMemo(() => cityMix.map((item) => ({
    ...item,
    percent: item.ocupacao,
    meta: `${currencylessNumber.format(item.ocupadas)} ocupadas · ${currencylessNumber.format(item.vagas)} vagas`,
  })), [cityMix]);

  const clusterBars = useMemo(() => (
    dashboardSnapshot.groupedRows.caixa
      .slice(0, 8)
      .map((item) => {
        const vagasNormalizadas = Number(item.vagas.toFixed(2));
        const vagasDisplay = Math.max(vagasNormalizadas, 0);
        const overflow = vagasNormalizadas < 0 ? Math.abs(vagasNormalizadas) : 0;

        return {
          label: item.label,
          shortLabel: buildCtoAxisLabel(item.label),
          cidade: item.cidade,
          projeto: item.projeto,
          pop: item.pop,
          ocupadas: Number(item.ocupadas.toFixed(2)),
          capacidade: Number(item.capacidade.toFixed(2)),
          vagas: vagasDisplay,
          vagasDisplay,
          overflow,
        };
      })
  ), [dashboardSnapshot]);

  const pieData = useMemo(() => ([
    { name: 'Portas ocupadas', value: summary.portasOcupadas, color: pieColors[0] },
    { name: 'Portas vagas', value: summary.portasVagas, color: pieColors[1] },
  ]), [summary]);

  const faixaSummary = useMemo(
    () => faixaSnapshot.faixaSummary.map((item) => ({
      ...item,
      label: STATUS_META[item.key].label,
      meta: `${item.count} CTOs`,
    })),
    [faixaSnapshot],
  );

  const topKpis = useMemo(() => [
    { label: 'CTOs monitoradas', value: summary.quantidadeCtos, icon: Orbit, hint: 'Estruturas com acompanhamento operacional' },
    { label: 'Portas ocupadas', value: summary.portasOcupadas, icon: Activity, hint: 'Clientes ativos utilizando capacidade' },
    { label: 'Portas vagas', value: summary.portasVagas, icon: Radar, hint: 'Capacidade livre para expansão' },
    { label: 'Total de portas', value: summary.totalPortas, icon: Network, hint: `Ocupação média de ${summary.ocupacaoMedia.toFixed(1)}%` },
  ], [summary]);

  const activeCities = useMemo(() => getActiveOccupancyFilterValues(filters, OCUPACAO_FILTER_FIELDS.cidade), [filters]);
  const activeProjects = useMemo(() => getActiveOccupancyFilterValues(filters, OCUPACAO_FILTER_FIELDS.projeto), [filters]);
  const activePops = useMemo(() => getActiveOccupancyFilterValues(filters, OCUPACAO_FILTER_FIELDS.pop), [filters]);
  const activeRanges = useMemo(() => getActiveOccupancyFilterValues(filters, OCUPACAO_FILTER_FIELDS.faixa_ocupacao), [filters]);

  if (loading && rowsRaw.length === 0) {
    return (
      <div className="mt-4 space-y-4">
        <div className={`${panelClassName} h-[184px] animate-pulse bg-[color:var(--surface-strong)]`} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`ocupacao-kpi-skeleton-${index}`} className={`${panelClassName} h-[136px] animate-pulse bg-[color:var(--surface-strong)]`} />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-5">
            <NetworkMap rows={[]} loading />
          </div>
          <div className="space-y-4 xl:col-span-7">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className={`${panelClassName} h-[280px] animate-pulse bg-[color:var(--surface-strong)]`} />
              <div className={`${panelClassName} h-[280px] animate-pulse bg-[color:var(--surface-strong)]`} />
            </div>
            <div className={`${panelClassName} h-[320px] animate-pulse bg-[color:var(--surface-strong)]`} />
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="mt-4">
        <div className={`${panelClassName} flex min-h-[240px] flex-col items-center justify-center gap-3 text-center`}>
          <AlertCircle className="h-8 w-8 text-rose-400" />
          <div>
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">Falha ao carregar ocupação de rede</p>
            <p className="mt-1 text-sm text-[color:var(--text-muted)]">{errorMessage}</p>
          </div>
          <button
            type="button"
            onClick={retry}
            className="mt-1 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition hover:bg-[color:var(--surface-muted)]"
            style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!loading && rowsRaw.length === 0) {
    return (
      <div className="mt-4">
        <div className={`${panelClassName} flex min-h-[260px] flex-col items-center justify-center gap-3 text-center`}>
          <Network className="h-8 w-8 text-[color:var(--text-muted)]" />
          <div>
            <p className="text-sm font-semibold text-[color:var(--text-primary)]">Nenhuma CTO encontrada</p>
            <p className="mt-1 text-sm text-[color:var(--text-muted)]">A API respondeu sem registros para a ação `cto_portas`.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className={`${panelClassName} flex flex-col gap-4 overflow-visible`}>
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[color:var(--text-primary)]">Filtros locais de ocupação</h2>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              Cidade, projeto, POP e faixa de ocupação refletem imediatamente no mapa e nos resumos da aba.
            </p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-muted)]">
              Os dropdowns fazem seleção rápida. Os painéis abaixo permitem cruzamento cumulativo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--text-muted)]">
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`${currencylessNumber.format(rowsFiltered.length)} CTOs visíveis`}
            </span>
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`${currencylessNumber.format(rowsRaw.length)} CTOs no dataset`}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <FilterSelect
            label="Cidade"
            value={(activeFiltersByField[OCUPACAO_FILTER_FIELDS.cidade] && activeFiltersByField[OCUPACAO_FILTER_FIELDS.cidade].length === 1) ? activeFiltersByField[OCUPACAO_FILTER_FIELDS.cidade][0] : ''}
            displayLabel={activeCities.length > 1 ? `${activeCities.length} cidades ativas` : undefined}
            helperText={activeCities.length > 1 ? 'Use os cards para seleção múltipla' : undefined}
            options={cidadeOptions}
            onChange={(value) => setFieldFilter(OCUPACAO_FILTER_FIELDS.cidade, value)}
          />
          <FilterSelect
            label="Projeto"
            value={(activeFiltersByField[OCUPACAO_FILTER_FIELDS.projeto] && activeFiltersByField[OCUPACAO_FILTER_FIELDS.projeto].length === 1) ? activeFiltersByField[OCUPACAO_FILTER_FIELDS.projeto][0] : ''}
            displayLabel={activeProjects.length > 1 ? `${activeProjects.length} projetos ativos` : undefined}
            helperText={activeProjects.length > 1 ? 'Use a lista para seleção múltipla' : undefined}
            options={projetoOptions}
            onChange={(value) => setFieldFilter(OCUPACAO_FILTER_FIELDS.projeto, value)}
          />
          <FilterSelect
            label="POP"
            value={(activeFiltersByField[OCUPACAO_FILTER_FIELDS.pop] && activeFiltersByField[OCUPACAO_FILTER_FIELDS.pop].length === 1) ? activeFiltersByField[OCUPACAO_FILTER_FIELDS.pop][0] : ''}
            displayLabel={activePops.length > 1 ? `${activePops.length} POPs ativos` : undefined}
            helperText={activePops.length > 1 ? 'Use a lista para seleção múltipla' : undefined}
            options={popOptions}
            onChange={(value) => setFieldFilter(OCUPACAO_FILTER_FIELDS.pop, value)}
          />
          <FilterSelect
            label="Faixa de ocupação"
            value={(activeFiltersByField[OCUPACAO_FILTER_FIELDS.faixa_ocupacao] && activeFiltersByField[OCUPACAO_FILTER_FIELDS.faixa_ocupacao].length === 1) ? activeFiltersByField[OCUPACAO_FILTER_FIELDS.faixa_ocupacao][0] : ''}
            displayLabel={activeRanges.length > 1 ? `${activeRanges.length} faixas ativas` : undefined}
            helperText={activeRanges.length > 1 ? 'Use o radar para seleção múltipla' : undefined}
            options={OCCUPANCY_RANGE_OPTIONS}
            onChange={(value) => setFieldFilter(OCUPACAO_FILTER_FIELDS.faixa_ocupacao, value)}
          />
        </div>

        {filters.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((filter) => (
              <button
                key={`${filter.field}-${filter.value}`}
                type="button"
                onClick={() => removeFilter(filter)}
                className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 px-3 py-1.5 text-xs font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)]"
              >
                <span>{filter.label || buildOccupancyFilterLabel(filter.field, filter.value)}</span>
                <X className="h-3.5 w-3.5 text-[color:var(--text-muted)]" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex min-h-[36px] items-center rounded-full border border-[color:var(--header-control-border-subtle)] px-3 py-1.5 text-xs font-semibold text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
            >
              Limpar filtros
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        <KpiCard
          label={topKpis[0].label}
          value={topKpis[0].value}
          hint={topKpis[0].hint}
          icon={topKpis[0].icon}
          accent="linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 72%, white 10%), transparent)"
        />
        <KpiCard
          label={topKpis[1].label}
          value={topKpis[1].value}
          hint={topKpis[1].hint}
          icon={topKpis[1].icon}
          accent="linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 82%, white 18%), transparent)"
        />
        <KpiCard
          label={topKpis[2].label}
          value={topKpis[2].value}
          hint={topKpis[2].hint}
          icon={topKpis[2].icon}
          accent="linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 76%, black 24%), transparent)"
        />
        <KpiCard
          label={topKpis[3].label}
          value={topKpis[3].value}
          hint={topKpis[3].hint}
          icon={topKpis[3].icon}
          accent="linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent) 68%, white 12%), transparent)"
        />
      </div>

      <OccupancyBandsOverview
        rows={faixaSummary}
        total={faixaSummary.reduce((total, item) => total + item.count, 0)}
        activeRanges={activeRanges}
        onToggleRangeFilter={(value) => toggleFilter({
          field: OCUPACAO_FILTER_FIELDS.faixa_ocupacao,
          value,
          label: buildOccupancyFilterLabel(OCUPACAO_FILTER_FIELDS.faixa_ocupacao, value),
        })}
      />

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.82fr)]">
        <div className="space-y-4">
          <NetworkMap rows={mapRows} loading={loading} isSyncing={isMapSyncing} />
          <div className={panelClassName}>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">CTOs com maior pressão</h3>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">Comparação direta entre utilização e capacidade livre dos pontos mais sensíveis.</p>
              </div>
              <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
                Operação de capacidade
              </span>
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clusterBars}
                  margin={isMobile ? { top: 8, right: 12, left: 0, bottom: 8 } : { top: 8, right: 20, left: 0, bottom: 8 }}
                  barCategoryGap={isMobile ? '18%' : '12%'}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                  <XAxis
                    dataKey="shortLabel"
                    tick={{ fontSize: isMobile ? 8 : 10 }}
                    stroke="var(--text-muted)"
                    interval={0}
                    angle={isMobile ? -24 : -18}
                    textAnchor="end"
                    height={isMobile ? 60 : 54}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 11 }} stroke="var(--text-muted)" />
                  <Tooltip content={<CapacityPressureTooltip />} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 12 }} iconSize={10} />
                  <Bar dataKey="ocupadas" name="Pontos ocupados" fill={ACCENT_SOFT} radius={[8, 8, 0, 0]} />
                  <Bar dataKey="vagas" name="Portas vagas" fill={ACCENT_STRONG} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className={`${panelClassName} min-h-[280px]`}>
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Composição de portas</h3>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">Divisão entre capacidade já utilizada e folga operacional de rede.</p>
            </div>
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={86} paddingAngle={3}>
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    labelStyle={{ color: 'var(--text-primary)' }}
                    itemStyle={{ color: 'var(--text-primary)' }}
                    formatter={(value) => currencylessNumber.format(Number(value || 0))}
                  />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 12, paddingTop: 16 }} iconSize={10} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <CityPressurePanel
            rows={occupancySummary}
            isMobile={isMobile}
            activeCities={activeCities}
            onToggleCityFilter={(value) => toggleFilter({
              field: OCUPACAO_FILTER_FIELDS.cidade,
              value,
              label: buildOccupancyFilterLabel(OCUPACAO_FILTER_FIELDS.cidade, value),
            })}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <ProjectOccupancyPanel
          rows={projectRows}
          activeProjects={activeProjects}
          onToggleProjectFilter={(value) => toggleFilter({
            field: OCUPACAO_FILTER_FIELDS.projeto,
            value,
            label: buildOccupancyFilterLabel(OCUPACAO_FILTER_FIELDS.projeto, value),
          })}
        />

        <PopOccupancyPanel
          rows={popRows}
          orderBy={popOrderBy}
          onOrderChange={(value) => setPopOrderBy(value || 'ocupadas')}
          viewMode={popViewMode}
          onViewModeChange={(value) => setPopViewMode(value || 'geral')}
          searchTerm={popSearch}
          onSearchTermChange={setPopSearch}
          activePops={activePops}
          onTogglePopFilter={(value) => toggleFilter({
            field: OCUPACAO_FILTER_FIELDS.pop,
            value,
            label: buildOccupancyFilterLabel(OCUPACAO_FILTER_FIELDS.pop, value),
          })}
        />
      </div>
    </div>
  );
};

const OcupacaoRedeDashboard = ({ isMobile = false }) => (
  <OcupacaoRedeDataProvider>
    <OcupacaoRedeDashboardContent isMobile={isMobile} />
  </OcupacaoRedeDataProvider>
);

export default OcupacaoRedeDashboard;
